/**
 * Rules about estimates themselves, as opposed to the money on them.
 *
 * The ladder lives in ladder.ts and is pinned by the golden tests. This is the
 * smaller question of what may be done to an estimate at all.
 */

import type { Estimate, Job } from "./types.js";

/**
 * Stages a job only reaches once the customer has bought something.
 *
 * Exported because `refuse_to_delete_a_sold_estimate` lists the same four in
 * SQL and the two have to agree — a stage added here and not there would offer
 * a delete button the database then refuses. `supabase/test` walks this list
 * against the trigger so the drift cannot happen quietly.
 */
export const WON_STAGES = [
  "signed",
  "in_production",
  "complete",
  "closed",
] as const;

/**
 * Has this job been sold?
 *
 * Three signals, any of which is enough, because the company reaches "sold" by
 * more than one route: the Signed Contract checklist step moves the stage, the
 * contract itself can be attached with a date, and handing the job to
 * production stamps `sends_at`. A job can be in production without anybody
 * having ticked the earlier boxes.
 */
export function jobIsWon(
  job: Pick<Job, "stage" | "signed_contract_at" | "sends_at">,
): boolean {
  return (
    (WON_STAGES as readonly string[]).includes(job.stage) ||
    Boolean(job.signed_contract_at) ||
    Boolean(job.sends_at)
  );
}

/**
 * May this estimate be deleted?
 *
 * Jonathon, 2026-08-05: "we need to be able to delete an estimate assuming it's
 * not closed won." Two ways an estimate counts as sold:
 *
 *   - it was signed, which sets `status` to approved; or
 *   - it is the active estimate on a job that has been won.
 *
 * The second is the one that does the work today, because nothing in the web
 * app writes `approved` — only the proposals acceptance trigger does, and that
 * has not merged. The losing options on a won job stay deletable: they are
 * precisely the junk this exists to clear away.
 *
 * A document the customer can still open is the fourth, and unlike the others
 * it is undoable: void the proposal and the estimate becomes deletable, which
 * is how a rep clears three sent options off a job (`canVoidDocument`). Pass
 * the estimate's documents and it is answered here; without them the card only
 * learns about it from the database.
 *
 * A paid order is the third refusal. That is money that has already left the
 * company and the payout record is the only trace of it — it should not arise on
 * an unsold estimate, and if it has, somebody wants to know before it disappears
 * behind a button. Pass the estimate's orders and it is checked here too; the
 * database refuses it either way (`refuse_to_delete_a_sold_estimate`), but a
 * button that is not offered beats one that fails with a Postgres exception.
 *
 * This function decides whether to offer the button; the trigger is what makes
 * the rule true.
 */
export function canDeleteEstimate(
  job: Pick<Job, "stage" | "signed_contract_at" | "sends_at">,
  estimate: Pick<Estimate, "status" | "is_active">,
  orders: readonly { status?: string | null | undefined }[] = [],
  documents: readonly EstimateDocument[] = [],
): boolean {
  return estimateDeleteBlocker(job, estimate, orders, documents) === null;
}

/**
 * A document this estimate has been offered on.
 *
 * `signed` is whether that document was ever signed — not whether it is signed
 * now. Voiding a signed proposal takes its force away and stands the estimate
 * back down (`on_proposal_voided`), but the acceptance survives as the record
 * of what was agreed, and `proposal_acceptances.estimate_id` is ON DELETE
 * RESTRICT. So an estimate somebody once signed can never be deleted, however
 * dead the proposal now is, and saying so here keeps that out of the user's
 * way as a foreign-key violation.
 */
export interface EstimateDocument {
  status?: string | null;
  signed?: boolean | null;
}

/**
 * The states in which a customer can still open the link they were sent.
 *
 * The same three the database refuses a delete over
 * (`refuse_to_delete_a_sold_estimate`); a draft, void, expired or declined
 * proposal is a dead packet and holds nothing.
 */
export const LIVE_PROPOSAL_STATUSES = ["sent", "viewed", "accepted"] as const;

/** Those documents the customer can still open. */
export function liveDocuments<T extends EstimateDocument>(
  documents: readonly T[],
): readonly T[] {
  return documents.filter((doc) =>
    (LIVE_PROPOSAL_STATUSES as readonly string[]).includes(
      (doc.status ?? "").trim(),
    ),
  );
}

/**
 * Why this estimate cannot be deleted, or null if it can be.
 *
 * One place rather than four, because the card has to say *which* rule is
 * holding — "void the proposal first" is a thing somebody can act on, and a
 * missing button is not.
 */
export type EstimateDeleteBlocker =
  "signed" | "sold" | "paid_order" | "live_document" | "was_signed";

export function estimateDeleteBlocker(
  job: Pick<Job, "stage" | "signed_contract_at" | "sends_at">,
  estimate: Pick<Estimate, "status" | "is_active">,
  orders: readonly { status?: string | null | undefined }[] = [],
  documents: readonly EstimateDocument[] = [],
): EstimateDeleteBlocker | null {
  if (estimate.status === "approved") return "signed";
  if (jobIsWon(job) && estimate.is_active) return "sold";
  if (hasPaidOrder(orders)) return "paid_order";
  if (liveDocuments(documents).length > 0) return "live_document";
  if (documents.some((doc) => doc.signed)) return "was_signed";
  return null;
}

/**
 * May this document be withdrawn?
 *
 * Jonathon, 2026-08-19: "we need to be able to unwind estimates that are sent
 * so we can delete estimates in order to keep their estimate builder clean."
 * Voiding is how: the packet stops being something the customer can open, the
 * estimate stands back down, and the junk options can then be cleared away.
 *
 * A document already void or expired has nothing left to withdraw — the link is
 * dead either way — so it is not offered, which keeps the button meaning one
 * thing. Everything else can be voided including a signed one: that is the only
 * remedy the database has after a signature (`on_proposal_voided`), and the
 * receipt survives it.
 */
export function canVoidDocument(status: string | null | undefined): boolean {
  const state = (status ?? "").trim();
  return state !== "" && state !== "void" && state !== "expired";
}

/**
 * Does the customer's own link still open?
 *
 * Not the same question as `canVoidDocument`, and the two were confused once:
 * a signed document can still be withdrawn, but its link is a one-way door and
 * refuses to open ever again (ADR 0032). Offering "Copy link" on one hands the
 * office something to send that greets the customer with "This has already
 * been signed" — the very refusal the read-back screen exists to get around.
 *
 * The list is `sent` and `viewed` and nothing else, which is the same rule
 * `openability` applies in supabase/functions/presented-document/validation.ts.
 * That copy is the one that decides; this one only decides whether to offer a
 * link, and `document-link-opens.test.ts` keeps the two in step.
 *
 * A draft has never been sent, an accepted or declined one is answered, and an
 * expiry in the past kills a link the status has not caught up with yet — the
 * endpoint checks the timestamp rather than waiting for the status, so this
 * does too.
 */
export function documentLinkOpens(
  status: string | null | undefined,
  expiresAt?: string | null,
  now: Date = new Date(),
): boolean {
  const state = (status ?? "").trim();
  if (state !== "sent" && state !== "viewed") return false;
  if (!expiresAt) return true;
  const expiry = new Date(expiresAt);
  if (Number.isNaN(expiry.getTime())) return true;
  return expiry >= now;
}

/**
 * Has any of these orders been paid out?
 *
 * Kept separate so the confirm dialog can say *why* the button is missing
 * rather than the estimate card silently having one fewer control on it.
 */
export function hasPaidOrder(
  orders: readonly { status?: string | null | undefined }[],
): boolean {
  return orders.some((order) => order.status === "paid");
}
